import { Injectable } from '@angular/core';
import { ServiceService, pbapiGetMetadataResponse, rpcStatus } from '../../../gen/syncui';
import { AuthService } from './auth.service';
import { MetadataStoreService } from './metadata-store.service';

export interface MetadataResult {
  success: boolean;
  error?: string;
  statusCode?: number;
  details?: string;
}

@Injectable({
  providedIn: 'root'
})
export class MetadataService {
  constructor(
    private serviceService: ServiceService,
    private authService: AuthService,
    private metadataStore: MetadataStoreService
  ) {}

  async getMetadata(): Promise<boolean> {
    return new Promise((resolve) => {
      this.serviceService.serviceGetMetadata().subscribe({
        next: (response) => {
          // Store the metadata response in the global store (only if it's not an rpcStatus)
          if (this.isMetadataResponse(response)) {
            this.metadataStore.setMetadata(response);
            resolve(true);
          } else {
            // Handle rpcStatus in next callback (unexpected but possible per type system)
            // This shouldn't happen in practice if API contract is correct, but we handle it defensively
            resolve(false);
          }
        },
        error: (error) => {
          // Check if it's a 401 Unauthorized response
          if (error.status === 401) {
            // Don't handle redirect here - let the AuthGuard handle it
            // This prevents infinite loops and ensures proper flow
            resolve(false);
          } else if (error.status === 403) {
            // User is authenticated but not authorized for this specific resource
            // Still allow navigation to FID picker page
            resolve(true);
          } else {
            resolve(false);
          }
        }
      });
    });
  }

  async getMetadataWithDetails(): Promise<MetadataResult> {
    return new Promise((resolve) => {
      this.serviceService.serviceGetMetadata().subscribe({
        next: (response) => {
          // Store the metadata response in the global store (only if it's not an rpcStatus)
          if (this.isMetadataResponse(response)) {
            this.metadataStore.setMetadata(response);
            resolve({ success: true });
          } else {
            // Handle rpcStatus in next callback (unexpected but possible per type system)
            const statusResponse = response as rpcStatus;
            resolve({
              success: false,
              error: statusResponse.message || 'Unexpected error response',
              statusCode: statusResponse.code,
              details: JSON.stringify(response, null, 2)
            });
          }
        },
        error: (error) => {
          const errorDetails = JSON.stringify(error, null, 2);

          // Check if it's a 401 Unauthorized response
          if (error.status === 401) {
            resolve({ 
              success: false, 
              error: error.error?.message || error.error?.detail || 'Authentication required. Please log in again.',
              statusCode: 401,
              details: errorDetails
            });
          } else if (error.status === 403) {
            resolve({ 
              success: true, 
              error: error.error?.message || error.error?.detail || 'Access restricted for this resource.',
              statusCode: 403,
              details: errorDetails
            });
          } else {
            resolve({ 
              success: false, 
              error: `Oops! Something went wrong. Please try again later.`,
              statusCode: error.status,
              details: errorDetails
            });
          }
        }
      });
    });
  }

  /**
   * Type guard to check if response is pbapiGetMetadataResponse (not rpcStatus)
   * 
   * Uses structural type checking based on the actual type definitions:
   * - pbapiGetMetadataResponse has a 'user' property (unique to this type)
   * - rpcStatus has 'code', 'message', and 'details' properties (unique to rpcStatus)
   * 
   * We check for the presence of 'user' AND absence of rpcStatus-specific properties
   * to reliably distinguish between the two types at runtime.
   */
  private isMetadataResponse(response: pbapiGetMetadataResponse | rpcStatus): response is pbapiGetMetadataResponse {
    // Check for properties unique to each type
    const hasUser = 'user' in response;
    const hasRpcStatusProperties = 'code' in response || 'message' in response;
    
    // If it has rpcStatus properties, it's definitely an rpcStatus
    if (hasRpcStatusProperties) {
      return false;
    }
    
    // If it has 'user' and no rpcStatus properties, it's a pbapiGetMetadataResponse
    return hasUser;
  }

}

